/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'

import {
  EuiButton,
  EuiFlexGroup,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiHorizontalRule,
  EuiSpacer,
  EuiText,
  EuiTitle,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import useFilters from '../useFilters'

import GroupSection from './GroupSection'
import FiltersSection from './FiltersSection'
import CSVSection from './CSVSection'

interface Props {
  toggleFlyout: () => void
  isLoading: boolean
}

const FiltersFlyout = ({ toggleFlyout, isLoading }: Props) => {
  const modalTitleId = useGeneratedHtmlId()
  const { filters } = useFilters()

  return (
    <EuiFlyout
      aria-labelledby={modalTitleId}
      ownFocus={true}
      onClose={toggleFlyout}
      data-test-id='billing-usage.flyout'
    >
      <EuiFlyoutHeader hasBorder={true}>
        <EuiTitle size='m'>
          <h2 id={modalTitleId}>
            <FormattedMessage id='billing-usage.filters.title' defaultMessage='View controls' />
          </h2>
        </EuiTitle>
        <EuiSpacer size='s' />
        <EuiText size='xs'>
          <FormattedMessage
            id='billing-usage.filters.subtitle'
            defaultMessage='Adjust the usage chart and table data'
          />
        </EuiText>
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        <EuiFlexGroup direction='column' gutterSize='m'>
          {!filters.instanceId && <GroupSection />}
          <EuiHorizontalRule margin='none' />
          <FiltersSection isLoading={isLoading} />
          <EuiHorizontalRule margin='none' />
          <CSVSection />
        </EuiFlexGroup>
      </EuiFlyoutBody>
      <EuiFlyoutFooter>
        <EuiButton
          onClick={toggleFlyout}
          fill={true}
          data-test-id='billing-usage.close-filters-button'
        >
          <FormattedMessage id='billing-usage.flyout.close' defaultMessage='Close' />
        </EuiButton>
      </EuiFlyoutFooter>
    </EuiFlyout>
  )
}

export default FiltersFlyout
